import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, adminVerify, adminLogout, adminSaveContent, adminReset, getToken } from "@/lib/adminApi";
import { useContent } from "@/lib/content";
import { LogOut, Save, RotateCcw, Plus, Trash2, Loader2, Eye, ChevronRight } from "lucide-react";

const SECTIONS = [
  { id: "topbar", label: "Felső sáv" },
  { id: "hero", label: "Hero" },
  { id: "about", label: "Rólunk" },
  { id: "weekly", label: "Heti menü" },
  { id: "menus", label: "Étlap & Itallap" },
  { id: "gallery", label: "Galéria" },
  { id: "reviews", label: "Vélemények" },
  { id: "contact", label: "Kapcsolat" },
  { id: "nav", label: "Navigáció" },
  { id: "footer", label: "Footer" },
  { id: "marquee", label: "Marquee" },
];

// ============ LOGIN ============
const LoginScreen = ({ onLoggedIn }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(password);
      onLoggedIn();
    } catch (err) {
      setError(err?.response?.data?.detail || "Sikertelen bejelentkezés");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-bg">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-surface border border-white/5 p-10"
      >
        <div className="font-heading text-4xl uppercase text-white mb-2">
          FACE <span className="text-brand">ROOM</span>
        </div>
        <div className="font-body text-xs uppercase tracking-[0.3em] text-brand mb-10">
          Admin felület
        </div>

        <label className="block font-body text-xs uppercase tracking-widest text-ink-muted mb-3">
          Jelszó
        </label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="w-full bg-bg border border-white/10 px-4 py-4 text-white font-body focus:border-brand focus:outline-none transition-colors"
          placeholder="••••••••••••"
        />

        {error && (
          <div data-testid="admin-login-error" className="mt-4 text-red-400 font-body text-sm">
            {error}
          </div>
        )}

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !password}
          className="mt-8 w-full bg-brand text-black font-heading uppercase tracking-wide py-4 hover:bg-brand-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : null}
          Belépés
        </button>
      </form>
    </div>
  );
};

// ============ FIELD COMPONENTS ============
const TextField = ({ label, value, onChange, multiline, testid }) => {
  const Comp = multiline ? "textarea" : "input";
  return (
    <div className="mb-5">
      <label className="block font-body text-xs uppercase tracking-widest text-ink-muted mb-2">
        {label}
      </label>
      <Comp
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={multiline ? 3 : undefined}
        className="w-full bg-bg border border-white/10 px-4 py-3 text-white font-body focus:border-brand focus:outline-none transition-colors resize-y"
      />
    </div>
  );
};

const ListField = ({ label, values, onChange, testid }) => {
  const arr = Array.isArray(values) ? values : [];
  const update = (i, v) => {
    const next = [...arr];
    next[i] = v;
    onChange(next);
  };
  const add = () => onChange([...arr, ""]);
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div className="mb-5">
      <label className="block font-body text-xs uppercase tracking-widest text-ink-muted mb-2">
        {label}
      </label>
      <div className="space-y-2">
        {arr.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              data-testid={`${testid}-item-${i}`}
              value={v || ""}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 bg-bg border border-white/10 px-4 py-2 text-white font-body focus:border-brand focus:outline-none"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="px-3 border border-white/10 text-ink-muted hover:text-red-400 hover:border-red-400 transition-colors"
              aria-label="Eltávolítás"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          data-testid={`${testid}-add`}
          className="flex items-center gap-2 text-brand font-body text-xs uppercase tracking-widest hover:text-brand-hover"
        >
          <Plus size={14} /> Hozzáadás
        </button>
      </div>
    </div>
  );
};

// ============ SECTION EDITORS ============
const HeroEditor = ({ data, set }) => (
  <>
    <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="hero-eyebrow-field" />
    <TextField label="Headline 1. sor" value={data.line1} onChange={(v) => set({ ...data, line1: v })} testid="hero-line1-field" />
    <TextField label="Headline 2. sor (narancs)" value={data.line2} onChange={(v) => set({ ...data, line2: v })} testid="hero-line2-field" />
    <TextField label="Headline 3. sor" value={data.line3} onChange={(v) => set({ ...data, line3: v })} testid="hero-line3-field" />
    <TextField label="Bevezető szöveg" value={data.lead} onChange={(v) => set({ ...data, lead: v })} multiline testid="hero-lead-field" />
    <TextField label="Elsődleges CTA" value={data.cta} onChange={(v) => set({ ...data, cta: v })} testid="hero-cta-field" />
  </>
);

const AboutEditor = ({ data, set }) => (
  <>
    <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="about-eyebrow-field" />
    <TextField label="Címsor" value={data.headline} onChange={(v) => set({ ...data, headline: v })} testid="about-headline-field" />
    <TextField label="1. bekezdés" value={data.p1} onChange={(v) => set({ ...data, p1: v })} multiline testid="about-p1-field" />
    <TextField label="2. bekezdés" value={data.p2} onChange={(v) => set({ ...data, p2: v })} multiline testid="about-p2-field" />
    <TextField label="3. bekezdés" value={data.p3} onChange={(v) => set({ ...data, p3: v })} multiline testid="about-p3-field" />
    <TextField label="Aláírás" value={data.sign} onChange={(v) => set({ ...data, sign: v })} testid="about-sign-field" />
  </>
);

const WeeklyEditor = ({ data, set }) => {
  const updateDay = (i, patch) => {
    const days = [...(data.days || [])];
    days[i] = { ...days[i], ...patch };
    set({ ...data, days });
  };
  return (
    <>
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="weekly-eyebrow-field" />
      <TextField label="Címsor" value={data.headline} onChange={(v) => set({ ...data, headline: v })} testid="weekly-headline-field" />
      <TextField label="Alcím / leírás" value={data.sub} onChange={(v) => set({ ...data, sub: v })} multiline testid="weekly-sub-field" />
      <TextField label="Extra menü címke" value={data.extraLabel} onChange={(v) => set({ ...data, extraLabel: v })} testid="weekly-extra-label-field" />
      <TextField label="Extra menü étel" value={data.extraDish} onChange={(v) => set({ ...data, extraDish: v })} testid="weekly-extra-dish-field" />
      <TextField label="„Leves” felirat" value={data.soup} onChange={(v) => set({ ...data, soup: v })} testid="weekly-soup-label-field" />
      <TextField label="„Főétel” felirat" value={data.main} onChange={(v) => set({ ...data, main: v })} testid="weekly-main-label-field" />
      <div className="mt-8 pt-8 border-t border-white/5">
        <div className="font-heading text-2xl uppercase text-white mb-6">Napok</div>
        {(data.days || []).map((day, i) => (
          <div key={i} className="mb-8 p-5 border border-white/5 bg-bg/50">
            <div className="font-body text-xs uppercase tracking-widest text-brand mb-4">Nap {i + 1}</div>
            <TextField label="Nap megnevezése (pl. „Hétfő · 2026.02.03”)" value={day.date} onChange={(v) => updateDay(i, { date: v })} testid={`weekly-day-${i}-date`} />
            <ListField label="Levesek" values={day.soups} onChange={(v) => updateDay(i, { soups: v })} testid={`weekly-day-${i}-soups`} />
            <ListField label="Főételek" values={day.mains} onChange={(v) => updateDay(i, { mains: v })} testid={`weekly-day-${i}-mains`} />
          </div>
        ))}
        <button
          type="button"
          onClick={() => set({ ...data, days: [...(data.days || []), { date: "", soups: [""], mains: [""] }] })}
          data-testid="weekly-add-day"
          className="flex items-center gap-2 text-brand font-body text-xs uppercase tracking-widest hover:text-brand-hover"
        >
          <Plus size={14} /> Új nap
        </button>
        {(data.days || []).length > 0 && (
          <button
            type="button"
            onClick={() => set({ ...data, days: (data.days || []).slice(0, -1) })}
            data-testid="weekly-remove-day"
            className="ml-6 flex items-center gap-2 text-ink-muted font-body text-xs uppercase tracking-widest hover:text-red-400 inline-flex"
          >
            <Trash2 size={14} /> Utolsó nap törlése
          </button>
        )}
      </div>
    </>
  );
};

const MenusEditor = ({ data, set }) => (
  <>
    <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="menus-eyebrow-field" />
    <TextField label="Címsor" value={data.headline} onChange={(v) => set({ ...data, headline: v })} testid="menus-headline-field" />
    <div className="mt-6 p-5 border border-white/5 bg-bg/50">
      <div className="font-body text-xs uppercase tracking-widest text-brand mb-4">Étlap</div>
      <TextField label="Cím" value={data.food?.title} onChange={(v) => set({ ...data, food: { ...data.food, title: v } })} testid="menus-food-title" />
      <TextField label="Leírás" value={data.food?.sub} onChange={(v) => set({ ...data, food: { ...data.food, sub: v } })} multiline testid="menus-food-sub" />
      <TextField label="Gomb felirat" value={data.food?.cta} onChange={(v) => set({ ...data, food: { ...data.food, cta: v } })} testid="menus-food-cta" />
      <TextField label="PDF URL" value={data.food?.url} onChange={(v) => set({ ...data, food: { ...data.food, url: v } })} testid="menus-food-url" />
    </div>
    <div className="mt-6 p-5 border border-white/5 bg-bg/50">
      <div className="font-body text-xs uppercase tracking-widest text-brand mb-4">Itallap</div>
      <TextField label="Cím" value={data.drinks?.title} onChange={(v) => set({ ...data, drinks: { ...data.drinks, title: v } })} testid="menus-drinks-title" />
      <TextField label="Leírás" value={data.drinks?.sub} onChange={(v) => set({ ...data, drinks: { ...data.drinks, sub: v } })} multiline testid="menus-drinks-sub" />
      <TextField label="Gomb felirat" value={data.drinks?.cta} onChange={(v) => set({ ...data, drinks: { ...data.drinks, cta: v } })} testid="menus-drinks-cta" />
      <TextField label="PDF URL" value={data.drinks?.url} onChange={(v) => set({ ...data, drinks: { ...data.drinks, url: v } })} testid="menus-drinks-url" />
    </div>
  </>
);

const GalleryEditor = ({ data, set }) => (
  <>
    <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="gallery-eyebrow-field" />
    <TextField label="Címsor" value={data.headline} onChange={(v) => set({ ...data, headline: v })} testid="gallery-headline-field" />
    <TextField label="Leírás" value={data.sub} onChange={(v) => set({ ...data, sub: v })} testid="gallery-sub-field" />
    <ListField label="Képek URL-jei" values={data.images} onChange={(v) => set({ ...data, images: v })} testid="gallery-images" />
  </>
);

const ReviewsEditor = ({ data, set }) => {
  const items = data.items || [];
  const update = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    set({ ...data, items: next });
  };
  return (
    <>
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="reviews-eyebrow-field" />
      <TextField label="Címsor" value={data.headline} onChange={(v) => set({ ...data, headline: v })} testid="reviews-headline-field" />
      <div className="mt-6 space-y-4">
        {items.map((r, i) => (
          <div key={i} className="p-5 border border-white/5 bg-bg/50">
            <div className="flex justify-between items-center mb-4">
              <div className="font-body text-xs uppercase tracking-widest text-brand">Vélemény {i + 1}</div>
              <button
                type="button"
                onClick={() => set({ ...data, items: items.filter((_, idx) => idx !== i) })}
                className="text-ink-muted hover:text-red-400"
                aria-label="Törlés"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <TextField label="Név (pl. „K. Anna”)" value={r.name} onChange={(v) => update(i, { name: v })} testid={`review-${i}-name`} />
            <TextField label="Város" value={r.city} onChange={(v) => update(i, { city: v })} testid={`review-${i}-city`} />
            <TextField label="Szöveg" value={r.text} onChange={(v) => update(i, { text: v })} multiline testid={`review-${i}-text`} />
          </div>
        ))}
        <button
          type="button"
          onClick={() => set({ ...data, items: [...items, { name: "", city: "", text: "" }] })}
          data-testid="reviews-add"
          className="flex items-center gap-2 text-brand font-body text-xs uppercase tracking-widest hover:text-brand-hover"
        >
          <Plus size={14} /> Új vélemény
        </button>
      </div>
    </>
  );
};

const ContactEditor = ({ data, set }) => {
  const hours = data.hoursTable || [];
  const updateHour = (i, patch) => {
    const next = [...hours];
    next[i] = { ...next[i], ...patch };
    set({ ...data, hoursTable: next });
  };
  return (
    <>
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set({ ...data, eyebrow: v })} testid="contact-eyebrow-field" />
      <TextField label="Címsor" value={data.headline} onChange={(v) => set({ ...data, headline: v })} testid="contact-headline-field" />
      <div className="grid md:grid-cols-2 gap-4">
        <TextField label="„Cím” felirat" value={data.address} onChange={(v) => set({ ...data, address: v })} testid="contact-addr-label" />
        <TextField label="Cím érték" value={data.addressValue} onChange={(v) => set({ ...data, addressValue: v })} testid="contact-addr-value" />
        <TextField label="„Asztalfoglalás” felirat" value={data.phone} onChange={(v) => set({ ...data, phone: v })} testid="contact-phone-label" />
        <TextField label="Telefon érték" value={data.phoneValue} onChange={(v) => set({ ...data, phoneValue: v })} testid="contact-phone-value" />
        <TextField label="„Email” felirat" value={data.email} onChange={(v) => set({ ...data, email: v })} testid="contact-email-label" />
        <TextField label="Email érték" value={data.emailValue} onChange={(v) => set({ ...data, emailValue: v })} testid="contact-email-value" />
        <TextField label="„Facebook” felirat" value={data.facebook} onChange={(v) => set({ ...data, facebook: v })} testid="contact-fb-label" />
        <TextField label="„GPS” felirat" value={data.gps} onChange={(v) => set({ ...data, gps: v })} testid="contact-gps-label" />
        <TextField label="GPS érték" value={data.gpsValue} onChange={(v) => set({ ...data, gpsValue: v })} testid="contact-gps-value" />
        <TextField label="„Nyitvatartás” felirat" value={data.hours} onChange={(v) => set({ ...data, hours: v })} testid="contact-hours-label" />
      </div>
      <div className="mt-6">
        <label className="block font-body text-xs uppercase tracking-widest text-ink-muted mb-3">
          Nyitvatartási idők
        </label>
        <div className="space-y-2">
          {hours.map((row, i) => (
            <div key={i} className="grid md:grid-cols-[1fr_1fr_auto] gap-2">
              <input
                data-testid={`hours-${i}-d`}
                value={row.d || ""}
                onChange={(e) => updateHour(i, { d: e.target.value })}
                placeholder="Nap (pl. Hétfő – Csütörtök)"
                className="bg-bg border border-white/10 px-4 py-2 text-white font-body focus:border-brand focus:outline-none"
              />
              <input
                data-testid={`hours-${i}-t`}
                value={row.t || ""}
                onChange={(e) => updateHour(i, { t: e.target.value })}
                placeholder="Idő (pl. 11:00 – 22:00)"
                className="bg-bg border border-white/10 px-4 py-2 text-white font-body focus:border-brand focus:outline-none"
              />
              <button
                type="button"
                onClick={() => set({ ...data, hoursTable: hours.filter((_, idx) => idx !== i) })}
                className="px-3 border border-white/10 text-ink-muted hover:text-red-400 hover:border-red-400 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => set({ ...data, hoursTable: [...hours, { d: "", t: "" }] })}
            className="flex items-center gap-2 text-brand font-body text-xs uppercase tracking-widest"
          >
            <Plus size={14} /> Új sor
          </button>
        </div>
      </div>
    </>
  );
};

const NavEditor = ({ data, set }) => (
  <>
    <TextField label="Rólunk" value={data.about} onChange={(v) => set({ ...data, about: v })} testid="nav-about" />
    <TextField label="Heti menü" value={data.menu} onChange={(v) => set({ ...data, menu: v })} testid="nav-menu" />
    <TextField label="Étlap & Itallap" value={data.menus} onChange={(v) => set({ ...data, menus: v })} testid="nav-menus" />
    <TextField label="Galéria" value={data.gallery} onChange={(v) => set({ ...data, gallery: v })} testid="nav-gallery" />
    <TextField label="Vélemények" value={data.reviews} onChange={(v) => set({ ...data, reviews: v })} testid="nav-reviews" />
    <TextField label="Kapcsolat" value={data.contact} onChange={(v) => set({ ...data, contact: v })} testid="nav-contact" />
  </>
);

const FooterEditor = ({ data, set }) => (
  <>
    <TextField label="Slogan / tagline" value={data.tagline} onChange={(v) => set({ ...data, tagline: v })} testid="footer-tagline" />
    <TextField label="Jogi szöveg" value={data.rights} onChange={(v) => set({ ...data, rights: v })} testid="footer-rights" />
  </>
);

const MarqueeEditor = ({ data, set }) => (
  <TextField label="Marquee szöveg" value={data} onChange={(v) => set(v)} multiline testid="marquee-field" />
);

const TopBarEditor = ({ data, set }) => {
  const sched = data.schedule || {};
  const labels = data.labels || {};
  const days = [
    { key: "mon", label: "Hétfő" },
    { key: "tue", label: "Kedd" },
    { key: "wed", label: "Szerda" },
    { key: "thu", label: "Csütörtök" },
    { key: "fri", label: "Péntek" },
    { key: "sat", label: "Szombat" },
    { key: "sun", label: "Vasárnap" },
  ];
  return (
    <>
      <label className="flex items-center gap-2 font-body text-xs uppercase tracking-widest text-white mb-6 cursor-pointer">
        <input
          data-testid="topbar-enabled"
          type="checkbox"
          checked={data.enabled !== false}
          onChange={(e) => set({ ...data, enabled: e.target.checked })}
          className="accent-brand w-4 h-4"
        />
        Felső státusz sáv megjelenítése a weboldalon
      </label>

      <TextField
        label="Egyedi üzenet (opcionális — pl. „Pénteken DJ est 20:00-tól!”)"
        value={data.message}
        onChange={(v) => set({ ...data, message: v })}
        testid="topbar-message-field"
      />

      <div className="mt-6 p-5 border border-white/5 bg-bg/50">
        <div className="font-body text-xs uppercase tracking-widest text-brand mb-4">
          Heti nyitvatartás (státusz automatikus számításához)
        </div>
        <p className="font-body text-xs text-ink-muted mb-4 leading-relaxed">
          Formátum: <span className="text-white font-semibold">HH:MM-HH:MM</span> (pl. 11:00-22:00).
          Hagyd üresen ha aznap ZÁRVA tartotok. Magyar idő (Budapest) szerint számolja a státuszt.
        </p>
        {days.map((d) => (
          <div key={d.key} className="grid grid-cols-[120px_1fr] gap-3 mb-3 items-center">
            <label className="font-body text-sm uppercase tracking-widest text-white">{d.label}</label>
            <input
              data-testid={`topbar-schedule-${d.key}`}
              value={sched[d.key] || ""}
              onChange={(e) => set({ ...data, schedule: { ...sched, [d.key]: e.target.value } })}
              placeholder="pl. 11:00-22:00 (vagy üres = zárva)"
              className="bg-bg border border-white/10 px-4 py-2 text-white font-body focus:border-brand focus:outline-none"
            />
          </div>
        ))}
      </div>

      <div className="mt-6 p-5 border border-white/5 bg-bg/50">
        <div className="font-body text-xs uppercase tracking-widest text-brand mb-4">Feliratok</div>
        <TextField label="„Nyitva” felirat" value={labels.open} onChange={(v) => set({ ...data, labels: { ...labels, open: v } })} testid="topbar-label-open" />
        <TextField label="„Zárva” felirat" value={labels.closed} onChange={(v) => set({ ...data, labels: { ...labels, closed: v } })} testid="topbar-label-closed" />
        <TextField label="„Ma” felirat" value={labels.today} onChange={(v) => set({ ...data, labels: { ...labels, today: v } })} testid="topbar-label-today" />
      </div>
    </>
  );
};


// ============ MAIN ADMIN PAGE ============
const AdminPage = () => {
  const navigate = useNavigate();
  const { refresh } = useContent();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hu, setHu] = useState(null);
  const [activeSection, setActiveSection] = useState("weekly");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [translateOnSave, setTranslateOnSave] = useState(true);

  // Auth check
  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      const ok = await adminVerify();
      setAuthed(ok);
      setChecking(false);
    })();
  }, []);

  // Load content
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${BACKEND_URL}/api/content`);
        const data = await res.json();
        setHu(data.hu);
      } catch (e) {
        setStatus({ type: "error", msg: "Tartalom betöltési hiba" });
      }
    })();
  }, [authed]);

  const setSection = (id, value) => {
    if (id === "marquee") {
      setHu({ ...hu, marquee: value });
    } else {
      setHu({ ...hu, [id]: value });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus({ type: "", msg: "" });
    try {
      await adminSaveContent(hu, translateOnSave);
      await refresh();
      setStatus({
        type: "success",
        msg: translateOnSave
          ? "Mentve és lefordítva ✓ Az élő weboldal frissült."
          : "Mentve ✓ (fordítás nélkül)",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setStatus({ type: "error", msg: `Mentési hiba: ${detail}` });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Biztosan visszaállítod az alapértelmezett tartalomra? Ez NEM vonható vissza.")) return;
    setSaving(true);
    try {
      await adminReset();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/content`);
      const data = await res.json();
      setHu(data.hu);
      await refresh();
      setStatus({ type: "success", msg: "Alapértelmezésekre visszaállítva ✓" });
    } catch (err) {
      setStatus({ type: "error", msg: "Visszaállítási hiba" });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await adminLogout();
    setAuthed(false);
    navigate("/");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Loader2 className="text-brand animate-spin" size={32} />
      </div>
    );
  }
  if (!authed) return <LoginScreen onLoggedIn={() => setAuthed(true)} />;
  if (!hu) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Loader2 className="text-brand animate-spin" size={32} />
      </div>
    );
  }

  const renderEditor = () => {
    switch (activeSection) {
      case "topbar": return <TopBarEditor data={hu.topbar || {}} set={(v) => setSection("topbar", v)} />;
      case "hero": return <HeroEditor data={hu.hero || {}} set={(v) => setSection("hero", v)} />;
      case "about": return <AboutEditor data={hu.about || {}} set={(v) => setSection("about", v)} />;
      case "weekly": return <WeeklyEditor data={hu.weekly || {}} set={(v) => setSection("weekly", v)} />;
      case "menus": return <MenusEditor data={hu.menus || {}} set={(v) => setSection("menus", v)} />;
      case "gallery": return <GalleryEditor data={hu.gallery || {}} set={(v) => setSection("gallery", v)} />;
      case "reviews": return <ReviewsEditor data={hu.reviews || {}} set={(v) => setSection("reviews", v)} />;
      case "contact": return <ContactEditor data={hu.contact || {}} set={(v) => setSection("contact", v)} />;
      case "nav": return <NavEditor data={hu.nav || {}} set={(v) => setSection("nav", v)} />;
      case "footer": return <FooterEditor data={hu.footer || {}} set={(v) => setSection("footer", v)} />;
      case "marquee": return <MarqueeEditor data={hu.marquee || ""} set={(v) => setSection("marquee", v)} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-bg/95 backdrop-blur-xl border-b border-white/5">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="font-heading text-xl uppercase">
              FACE <span className="text-brand">ROOM</span>
            </div>
            <div className="hidden md:block font-body text-xs uppercase tracking-[0.25em] text-ink-muted">
              Admin · Tartalomkezelő
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-view-site"
              className="hidden sm:flex items-center gap-2 px-4 py-2 border border-white/10 text-white font-body text-xs uppercase tracking-widest hover:border-brand hover:text-brand transition-colors"
            >
              <Eye size={14} /> Élő oldal
            </a>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white font-body text-xs uppercase tracking-widest hover:border-red-400 hover:text-red-400 transition-colors"
            >
              <LogOut size={14} /> Kilépés
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row max-w-[1600px] mx-auto">
        {/* Sidebar */}
        <aside className="lg:w-64 lg:min-h-[calc(100vh-65px)] border-b lg:border-b-0 lg:border-r border-white/5 p-4 lg:p-6">
          <div className="font-body text-xs uppercase tracking-[0.25em] text-ink-muted mb-4">Szekciók</div>
          <nav className="grid grid-cols-2 lg:grid-cols-1 gap-1">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                data-testid={`admin-tab-${s.id}`}
                onClick={() => setActiveSection(s.id)}
                className={`flex items-center justify-between px-4 py-3 font-body text-sm uppercase tracking-wider transition-colors text-left ${
                  activeSection === s.id
                    ? "bg-brand text-black"
                    : "text-white/80 hover:bg-surface hover:text-brand"
                }`}
              >
                <span>{s.label}</span>
                {activeSection === s.id && <ChevronRight size={14} />}
              </button>
            ))}
          </nav>
        </aside>

        {/* Editor */}
        <main className="flex-1 p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <div className="font-body text-xs uppercase tracking-[0.3em] text-brand mb-2">
                  Szerkesztés
                </div>
                <h1 className="font-heading text-4xl md:text-5xl uppercase">
                  {SECTIONS.find((s) => s.id === activeSection)?.label}
                </h1>
              </div>
              <label className="flex items-center gap-2 font-body text-xs uppercase tracking-widest text-ink-muted cursor-pointer">
                <input
                  data-testid="translate-toggle"
                  type="checkbox"
                  checked={translateOnSave}
                  onChange={(e) => setTranslateOnSave(e.target.checked)}
                  className="accent-brand w-4 h-4"
                />
                AI fordítás EN-re mentéskor
              </label>
            </div>

            <div data-testid="admin-editor-content" className="bg-surface/40 border border-white/5 p-6 md:p-8">
              {renderEditor()}
            </div>

            {/* Status */}
            {status.msg && (
              <div
                data-testid="admin-status"
                className={`mt-6 p-4 border font-body text-sm ${
                  status.type === "success"
                    ? "border-brand/40 bg-brand/10 text-brand"
                    : "border-red-400/40 bg-red-400/10 text-red-400"
                }`}
              >
                {status.msg}
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="admin-save"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-brand text-black font-heading uppercase tracking-wide px-8 py-4 hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {saving ? "Mentés…" : "Mentés & Élesítés"}
              </button>
              <button
                data-testid="admin-reset"
                onClick={handleReset}
                disabled={saving}
                className="flex items-center gap-2 border border-white/10 text-white font-heading uppercase tracking-wide px-6 py-4 hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-50"
              >
                <RotateCcw size={16} /> Alapértelmezés
              </button>
            </div>

            <p className="mt-6 font-body text-xs text-ink-dim leading-relaxed">
              💡 Csak a magyar tartalmat szerkeszted. Az angol verzió automatikusan készül a mentéskor
              (Claude Sonnet 4.5). A változások azonnal megjelennek az élő weboldalon.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
